import { AccountProperties, AccountSettings, ConnectionState, ConnectionType } from "../../types/AccountSettings";
import { HttpException } from "../../types/Exceptions";
import { HttpService } from "./HttpService";
import { fetchWithRetry } from "../../utils/fetch-utils";
import { LocalHealthData } from "../../types/LocalHealthData";

export type TimePeriod = 'latest' | 'lastWeek' | 'lastMonth' | 'lastYear' | 'all';

export default class AccountHttpService extends HttpService {
    public async getProperties() {
    const res = await fetchWithRetry(`${this.baseAccountPath}`, {
            method: 'GET',
            headers: {
                ...this.commonHeaders
            },
            credentials: 'include'
        });

        const body = await res.json();

        if (res.status !== 200) {
            HttpService.throwHttpError(res, body);
        }          
    
          const settings = body as AccountProperties;

          return settings;
    }

    public async updateSettings(settings: AccountSettings) {
    const res = await fetchWithRetry(`${this.baseAccountPath}/settings`, {
            method: 'PATCH',
            headers: {
                ...this.commonHeaders,
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(settings),
            credentials: 'include'
        });

        if (res.status !== 200) {
            const body = await res.json();
            HttpService.throwHttpError(res, body);
        }        
    }    

    public async syncConnections(
        type: ConnectionType | "all",
        period: TimePeriod,
        localSleepData?: LocalHealthData
    ) {
        let url = `${this.baseAccountPath}/connectedAccounts/sync`;
        if (type !== "all") {
            url += `/${type}`;
        }

        url += `?period=${period}`;
        
    let res = await fetchWithRetry(url, {
            method: 'POST',
            headers: {
                ...this.commonHeaders,
                'Content-Type': 'application/json',
            },
            credentials: 'include',
            body: localSleepData ? JSON.stringify(localSleepData) : undefined
        });

        const body = await res.json();

        if (res.status !== 200) {
            HttpService.throwHttpError(res, body);
        }
        
        const syncStatus = body as ConnectionState[];
        return syncStatus;
    }

    public async disconnectConnection(type: ConnectionType) {
        const url = `${this.baseAccountPath}/connectedAccounts/${type}`;   
        
    let res = await fetchWithRetry(url, {
            method: 'DELETE',
            headers: {
                ...this.commonHeaders,
            },
            credentials: 'include'
        });

        if (res.status !== 200) {
            const body = await res.json();
            HttpService.throwHttpError(res, body);  
        }
    }

    public async cancelSync(failInProgressSyncs: boolean) {
        const url = `${this.baseAccountPath}/connectedAccounts/cancelSync?failInProgressSyncs=${failInProgressSyncs}`;   
        
    let res = await fetchWithRetry(url, {
            method: 'POST',
            headers: {
                ...this.commonHeaders,
            },
            credentials: 'include'
        });

        if (res.status !== 200) {
            const body = await res.json();
            HttpService.throwHttpError(res, body);
        }        
    }

    public async createSession(username: string, password: string) {
    const res = await fetchWithRetry(`${AccountHttpService.basePath}/accounts/${username}/sessions`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                ...this.commonHeaders,
            },
            body: JSON.stringify({username, password}),
            credentials: 'include'
        });
        
        if (res.status !== 201) {
            const body = await res.json();
            HttpService.throwHttpError(res, body);
        }         
    }

    public async deleteSession() {
    const res = await fetchWithRetry(`${this.baseAccountPath}/session`, {
            method: 'DELETE',
            headers: {
                ...this.commonHeaders,
            },
            credentials: 'include'
        });
        
        if (res.status !== 200) {
            const body = await res.json();
            HttpService.throwHttpError(res, body);
        }             
    }

    public async clearSessionCookie() {
        const url = `${this.baseAccountPath}/sessions/cookie`;
        
    let res = await fetchWithRetry(url, {
            method: 'DELETE',
            headers: {
                ...this.commonHeaders,
            },
            credentials: 'include'
        });

        if (res.status !== 200) {
            const body = await res.json();
            HttpService.throwHttpError(res, body);  
        }            
    }

    public async updatePassword(currentPassword: string, newPassword: string) {
        const url = `${this.baseAccountPath}/password`;

    const res = await fetchWithRetry(url, {
            method: 'PATCH',
            headers: {
                ...this.commonHeaders,
                'Content-Type': 'application/json'
            },
            body: JSON.stringify({password: currentPassword, newPassword: newPassword}),
            credentials: 'include'
        });
    
        if (res.status !== 200) {
            const body = await res.json();
            HttpService.throwHttpError(res, body);
        }     
    }

    public async updateEmail(currentPassword: string, newEmail: string) {
        const url = `${this.baseAccountPath}/email`;

    const res = await fetchWithRetry(url, {
            method: 'PATCH',
            headers: {
                ...this.commonHeaders,
                'Content-Type': 'application/json'
            },
            body: JSON.stringify({password: currentPassword, newEmail: newEmail}),
            credentials: 'include'
        });
    
        if (res.status !== 200) {
            const body = await res.json();
            HttpService.throwHttpError(res, body);
        }     
    }
    
    public async sendResetPasswordEmail(accountName: string) {
        const url = `${this.baseEmailServicePath}/resetPassword`;

    const res = await fetchWithRetry(url, {
            method: 'POST',
            headers: {
                ...this.commonHeaders,
                'Content-Type': 'application/json'
            },
            body: JSON.stringify({ accountName: accountName }),
            credentials: 'include'
        });
    
        if (res.status !== 200) {
            const body = await res.json();
            HttpService.throwHttpError(res, body);
        }          
    }

    public async resetPassword(accountName: string, newPassword: string, token: any, signature: string) {
        const url = `${this.baseAccountPath}/password/reset`;

    const res = await fetchWithRetry(url, {
            method: 'POST',
            headers: {
                ...this.commonHeaders,
                'Content-Type': 'application/json'
            },
            body: JSON.stringify({ token: token, signature: signature, newPassword: newPassword }),
            credentials: 'include'
        });
    
        if (res.status !== 200) {
            const error = await res.json();
            throw new HttpException(res.status, error.error, error.msg);
        }          
    }

    /**
     * Gets the Google Health authorization URL. The server keeps the CSRF state and PKCE verifier in the session.
     */
    public async getGoogleHealthAuthorizationUrl(): Promise<string> {
        const res = await fetchWithRetry(`${this.baseAccountPath}/connectedAccounts/googleHealth/authorizationUrl`, {
            method: 'POST',
            headers: {
                ...this.commonHeaders,
            },
            credentials: 'include'
        });

        const body = await res.json();

        if (res.status !== 200) {
            HttpService.throwHttpError(res, body);
        }

        return (body as { url: string }).url;
    }

    public async addConnection(source: ConnectionType, code: string | undefined, state?: string) {
    const res = await fetchWithRetry(`${this.baseAccountPath}/connectedAccounts`, {
            method: 'POST',
            headers: {
                ...this.commonHeaders,
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({
                code: code,
                source: source,
                state: state
            }),
            credentials: 'include'
        });
        
        if (res.status !== 201) {
            const body = await res.json();
            HttpService.throwHttpError(res, body);
        }   
    }

    public async createAccount(username: string, password: string, email: string) {
    const res = await fetchWithRetry(`${AccountHttpService.basePath}/accounts`, {
            method: 'POST',
            headers: {
                ...this.commonHeaders,
                'Content-Type': 'application/json'
            },
            body: JSON.stringify({
                username: username,
                password: password,
                email: email
            }),
            credentials: 'include'
        });
        
        if (res.status !== 201) {
            const body = await res.json();
            HttpService.throwHttpError(res, body);
        }
    }

    private readonly baseEmailServicePath = `${AccountHttpService.basePath}/emailService`;
}